import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { findFirstPartyMcpBySlug } from '@wix/ai-scheduling-assistant-constants';
import { useGenericBi } from './use-generic-bi';
import { useTranslation } from './i18n';
import { useProductName } from './use-brand';
import { CONNECTORS_PAGE, connectorClickFields } from './connectors-bi';
import { connectorConnectRoute } from './connector-connect-route';
import { disconnectConfirmKey } from './disconnect-confirm-key';
import {
  authorizeConnector,
  calendarProviderToProviderId,
  disconnectCalendarAccounts,
  disconnectConnector,
  fetchConnectors,
  type ConnectorStatus,
} from './connectors';
import { PROVIDERS, useCalendarConnect, type Provider } from '../components/CalendarConnect';
import { chatHandoffLink, type ChatHandoffNew } from './chat-handoff';
import { deleteMcpConnection } from './mcp';
import { useBasePath } from './use-base-path';
import { closePopup } from './close-popup';
import { useConfirm } from '../components/ConfirmDialog';
import { APPS_SCRIPT_SETUP_PATH } from './apps-script-setup';

// The connect/disconnect RAIL DISPATCH, extracted so both the Connectors page (via useAppsController)
// and the empty-chat suggested-task CONNECT chip share ONE implementation of it. There is exactly one
// `connectorConnectRoute` branch in the app, here — a chip that "connects" must run the same
// composio/native-calendar/assistant-chat flow the Connectors page does, not a second copy of it.
//
// It owns the connect subsystem's state (busy/error/the inline Apple form) and the popup + poll timers,
// but NOT the connectors list itself: the caller keeps that (useAppsController in local state, the chip
// in the shared React Query cache) and hands this hook `getConnectors` to read the add-another baseline
// and `refresh` to re-read after a connect. `onConnected` arms the assistant-wake (see lib/connect-handoff)
// — the chat surface passes it so the genome's turn is awaited; the Connectors page omits it.

// ACTIVE accounts on a row — drives add-another success detection (count rising over the pre-popup
// baseline, covering both first connect 0→1 and add-another 1→2). Falls back to the connected boolean.
export function accountCountFor(connector: ConnectorStatus | undefined): number {
  return connector?.accounts?.length ?? (connector?.connected ? 1 : 0);
}

// Composio lands its OAuth callback on its own servers — the popup never
// messages back — so polling status is the only signal a connect completed.
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 80;

export interface ConnectConnector {
  /**
   * The keys with an action in flight — a toolkit for a connect / reconnect / whole-row disconnect, an
   * account id for a single-account removal. A SET, not one key, and that is the whole point: a connect
   * is an OAuth popup the user drives, so every other row has to stay pressable while it is open.
   *
   * It used to be one `string | null`, and every card read `busy !== null` as its own `disabled` — so one
   * tap greyed out the entire screen until that popup resolved (reported: "every time user taps connect,
   * all the connectors change their bg color to grey and get disabled"). Both phones were already per-row
   * (`store.connectingToolkit == conn.toolkit`); web was the outlier.
   *
   * A row asks about ITSELF — `busy.has(connector.toolkit)` — and never about the set being non-empty.
   */
  busy: ReadonlySet<string>;
  actionError: string | null;
  // Apple connects with an app-specific password (not OAuth) — its card expands an inline form.
  icloudOpenFor: string | null;
  appleId: string;
  applePassword: string;
  setAppleId: (v: string) => void;
  setApplePassword: (v: string) => void;
  submitICloud: () => void;
  connect: (connector: ConnectorStatus) => void;
  addAnother: (connector: ConnectorStatus) => void;
  disconnect: (connector: ConnectorStatus, name: string, accountId?: string) => void;
}

export function useConnectConnector({
  getConnectors,
  getGmailScopesEnabled,
  refresh,
  onConnected,
  openChat,
}: {
  /** The current connectors snapshot — read at connect time for the add-another baseline. */
  getConnectors: () => ConnectorStatus[];
  /** Whether a Google connect should also ask for the Gmail scopes, per the server's default-off gate.
   *  Omitted ⇒ calendar scopes only, which is the right answer for any surface that has not read the
   *  status: the Gmail scopes are unapproved, and consenting to one spends an unrecoverable cap slot. */
  getGmailScopesEnabled?: () => boolean;
  /** Re-read the connectors list into whatever store the caller keeps it in (local state / RQ cache). */
  refresh: () => Promise<void>;
  /** Arm the assistant-wake after a successful connect, with the connector's display name (drives the
   *  "Inspecting your {name}." handoff placeholder). The chat surface passes `noteConnected`; the
   *  Connectors page omits it (no conversation to wake). */
  onConnected?: (name: string) => void;
  /** How an assistant-chat connect (a first-party MCP / wix-service row) opens its seeded chat. Omitted ⇒
   *  a route push to the home chat (`chatHandoffLink`). A SETTINGS screen passes `useOpenSideChat()` so the
   *  chat opens IN PLACE over the screen the reader is on, instead of throwing it away — the rule in
   *  `.claude/rules/orion-web-agents-tasks.md`; the Outreach tab's Alfred row is the first caller. */
  openChat?: (handoff: ChatHandoffNew) => void;
}): ConnectConnector {
  const productName = useProductName();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { logPageClickBi } = useGenericBi();
  // The ref is the authority and the state is its mirror: a click handler claims its key SYNCHRONOUSLY,
  // so two taps landing in one tick cannot both pass the guard, and the callbacks below never have to
  // depend on `busy` (which is what kept their identities stable before).
  const busyRef = useRef<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<ReadonlySet<string>>(busyRef.current);
  const claimBusy = useCallback((key: string) => {
    if (busyRef.current.has(key)) return false;
    busyRef.current = new Set(busyRef.current).add(key);
    setBusy(busyRef.current);
    return true;
  }, []);
  const releaseBusy = useCallback((key: string) => {
    const next = new Set(busyRef.current);
    next.delete(key);
    busyRef.current = next;
    setBusy(next);
  }, []);
  const [actionError, setActionError] = useState<string | null>(null);
  // One entry per flow in flight, so an unmount cleans up every popup and poll rather than only the last
  // one started. Each poll still clears its OWN interval on the way out; these are the cleanup handles.
  const pollTimersRef = useRef(new Set<ReturnType<typeof setInterval>>());
  const popupsRef = useRef(new Map<string, Window>());
  // Native calendar rows reuse the settings/onboarding calendar plumbing: OAuth popup for
  // Google/Outlook, the inline app-password form for Apple.
  const { connectOAuth, connectICloud } = useCalendarConnect({ getGmailScopesEnabled });
  const [icloudOpenFor, setIcloudOpenFor] = useState<string | null>(null);
  const [appleId, setAppleId] = useState('');
  const [applePassword, setApplePassword] = useState('');
  // First-party MCP rows (WordPress) connect via a chat handoff (the assistant runs the OAuth dance
  // through ManageMcp) — needs the router + mount prefix to build the handoff link imperatively.
  const router = useRouter();
  const basePath = useBasePath();

  // Read the caller's inputs through refs so the connect callbacks keep stable identities regardless of
  // how the caller sources the connectors list — the click handlers below are never in a fetching effect,
  // but a stable identity keeps them cheap to pass down and impossible to couple into one.
  const getConnectorsRef = useRef(getConnectors);
  getConnectorsRef.current = getConnectors;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const openChatRef = useRef(openChat);
  openChatRef.current = openChat;

  useEffect(() => {
    const timers = pollTimersRef.current;
    const popups = popupsRef.current;
    return () => {
      timers.forEach((timer) => clearInterval(timer));
      timers.clear();
      popups.forEach(closePopup);
      popups.clear();
    };
  }, []);

  // Resolve once the toolkit's ACTIVE account count rises above `baseline` — works for a first connect
  // (0→1) and for adding another account (1→2) alike.
  const pollUntilAccountAdded = useCallback((toolkit: string, baseline: number, popup: Window) => {
    return new Promise<void>((resolve, reject) => {
      let attempts = 0;
      let inFlight = false;
      const stop = () => {
        clearInterval(timer);
        pollTimersRef.current.delete(timer);
      };
      const timer = setInterval(async () => {
        if (popup.closed) {
          stop();
          reject(new Error('popup_closed'));
          return;
        }
        if (inFlight) return;
        attempts += 1;
        if (attempts > MAX_POLL_ATTEMPTS) {
          stop();
          resolve();
          return;
        }
        inFlight = true;
        try {
          const { connectors } = await fetchConnectors();
          if (accountCountFor(connectors.find((c) => c.toolkit === toolkit)) > baseline) {
            stop();
            resolve();
          }
        } catch {
          // transient read failure — keep polling within the cap
        } finally {
          inFlight = false;
        }
      }, POLL_INTERVAL_MS);
      pollTimersRef.current.add(timer);
    });
  }, []);

  // Native calendar connect: OAuth popup (Google/Outlook) returns to chat.nownia.com which postMessages
  // success; Apple expands an inline app-password form instead. Refreshes the list on success.
  const connectNative = useCallback(
    async (connector: ConnectorStatus) => {
      if (busyRef.current.has(connector.toolkit)) return;
      // Named from the row's own verdict, so a repair is countable apart from a first connect. It is
      // named HERE and not on the Reconnect button because that button runs this same action — raising
      // the event there reports two names for one tap, and leaves `connect_` conflating the two.
      logPageClickBi(
        { ...CONNECTORS_PAGE, button_name: `${connector.needsReconnect === true ? 'reconnect' : 'connect'}_${connector.toolkit}` },
        { additionalFields: connectorClickFields(connector) },
      );
      const providerId = calendarProviderToProviderId(connector.calendarProvider);
      if (providerId === 'apple') {
        setActionError(null);
        setIcloudOpenFor((open) => (open === connector.toolkit ? null : connector.toolkit));
        return;
      }
      const provider = PROVIDERS.find((p): p is Provider => p.id === providerId);
      if (!provider || provider.kind !== 'oauth') {
        setActionError("Couldn't start the calendar connect. Please try again.");
        return;
      }
      setIcloudOpenFor(null); // close a lingering Apple form when connecting a different provider
      if (!claimBusy(connector.toolkit)) return;
      setActionError(null);
      try {
        const { connected, errorKind } = await connectOAuth(provider);
        if (connected) {
          await refreshRef.current();
          onConnectedRef.current?.(connector.name ?? '');
        } else if (errorKind === 'missing_permission') {
          // The one failure the user can act on: Google let them finish the flow without ticking the
          // calendar permission, so the row has to say which permission to allow rather than nothing.
          setActionError(t('calendar.connect.error.missingPermission'));
        }
      } catch {
        setActionError("Couldn't reach the calendar service. Please try again.");
      } finally {
        releaseBusy(connector.toolkit);
      }
    },
    [claimBusy, connectOAuth, logPageClickBi, releaseBusy, t],
  );

  const submitICloud = useCallback(async () => {
    if (!claimBusy('calendar-apple')) return;
    setActionError(null);
    try {
      if (await connectICloud(appleId, applePassword)) {
        const appleName = getConnectorsRef.current().find((c) => c.toolkit === icloudOpenFor)?.name ?? '';
        setIcloudOpenFor(null);
        setAppleId('');
        setApplePassword('');
        await refreshRef.current();
        onConnectedRef.current?.(appleName);
      }
    } catch {
      setActionError("Couldn't connect that Apple account. Please try again.");
    } finally {
      releaseBusy('calendar-apple');
    }
  }, [claimBusy, connectICloud, appleId, applePassword, icloudOpenFor, releaseBusy]);

  // Composio connect — first connect, or "add another account" (allowMultiple). Opens the OAuth popup
  // and polls until the toolkit's account count rises over its pre-popup baseline.
  const connectComposio = useCallback(
    async (toolkit: string, allowMultiple = false) => {
      if (busyRef.current.has(toolkit)) return;
      setIcloudOpenFor(null); // close a lingering Apple form when connecting a Composio app
      logPageClickBi(
        { ...CONNECTORS_PAGE, button_name: `${allowMultiple ? 'add_another' : 'connect'}_${toolkit}` },
        { additionalFields: connectorClickFields(getConnectorsRef.current().find((c) => c.toolkit === toolkit), toolkit) },
      );
      if (!claimBusy(toolkit)) return;
      setActionError(null);
      const baseline = accountCountFor(getConnectorsRef.current().find((c) => c.toolkit === toolkit));
      let popup: Window | null = null;
      try {
        const result = await authorizeConnector(toolkit, allowMultiple);
        if (result.alreadyConnected) {
          await refreshRef.current();
          return;
        }
        if (!result.url) {
          throw new Error(result.error || 'no_auth_url');
        }
        // The window NAME is per toolkit on purpose. With one shared name a second connect would
        // RE-USE the first's popup — navigating it away mid-OAuth and killing that flow — which is the
        // other half of why only one connect at a time was ever possible here.
        popup = window.open(result.url, `orion-connector-oauth-${toolkit}`, 'width=520,height=720');
        if (!popup) {
          throw new Error('popup_blocked');
        }
        popupsRef.current.set(toolkit, popup);
        await pollUntilAccountAdded(toolkit, baseline, popup);
        await refreshRef.current();
        onConnectedRef.current?.(getConnectorsRef.current().find((c) => c.toolkit === toolkit)?.name ?? '');
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message === 'popup_blocked') {
          setActionError("Couldn't open the connect window. Check your browser's popup blocker.");
        } else if (message === 'popup_closed') {
          await refreshRef.current();
        } else {
          setActionError("Couldn't reach the connector service. Please try again.");
        }
      } finally {
        closePopup(popup);
        popupsRef.current.delete(toolkit);
        releaseBusy(toolkit);
      }
    },
    [claimBusy, logPageClickBi, pollUntilAccountAdded, releaseBusy],
  );

  // First-party MCP connect: no popup/poll — the OAuth dance happens in chat (ManageMcp hands the
  // user an authorize link), so we drop them into a fresh thread with the registry's pre-filled
  // prompt naming the server and its canonical URL.
  //
  // Also the destination for any row the server sent `connectVia: 'assistant-chat'` on, whatever its
  // provider. Such a row need not be in the registry, so the prompt falls back to naming the connector —
  // the same sentence the phones build. The missing-entry refusal still stands for a row routed here by
  // its PROVIDER, where an unresolvable slug really is a broken row rather than a rail we were told about.
  const connectMcp = useCallback(
    (connector: ConnectorStatus) => {
      // No busy key: this rail opens a seeded chat and returns. It used to sit behind the global lock,
      // which only ever meant "another connector's popup is open", and that is not a reason to refuse.
      const firstParty = findFirstPartyMcpBySlug(connector.toolkit);
      if (!firstParty && connector.connectVia !== 'assistant-chat') {
        setActionError("Couldn't start the connect. Please try again.");
        return;
      }
      const connectPrompt = firstParty?.connectPrompt ?? `I want to connect ${connector.name || connector.toolkit}.`;
      setIcloudOpenFor(null); // close a lingering Apple form when connecting an MCP server
      logPageClickBi(
        { ...CONNECTORS_PAGE, button_name: `connect_${connector.toolkit}` },
        { additionalFields: connectorClickFields(connector) },
      );
      if (openChatRef.current) {
        openChatRef.current({ kind: 'new', message: connectPrompt });
        return;
      }
      const handoff = chatHandoffLink({ kind: 'new', message: connectPrompt }, basePath);
      void router.push(handoff.route, handoff.path);
    },
    [basePath, logPageClickBi, router],
  );

  const connect = useCallback(
    (connector: ConnectorStatus) =>
      connector.toolkit === 'google-apps-script'
        ? void router.push(`${basePath}${APPS_SCRIPT_SETUP_PATH}`)
        : connectorConnectRoute(connector) === 'assistant-chat'
        ? void connectMcp(connector)
        : connectorConnectRoute(connector) === 'native-calendar'
          ? void connectNative(connector)
          : void connectComposio(connector.toolkit),
    [basePath, router, connectNative, connectMcp, connectComposio],
  );

  // Link an additional account on an already-connected row. Native (calendar) rows re-run the native
  // connect flow (which already supports several calendars); Composio rows mint another link with allowMultiple.
  const addAnother = useCallback(
    (connector: ConnectorStatus) =>
      connectorConnectRoute(connector) === 'assistant-chat'
        ? void connectMcp(connector)
        : connectorConnectRoute(connector) === 'native-calendar'
          ? void connectNative(connector)
          : void connectComposio(connector.toolkit, true),
    [connectNative, connectMcp, connectComposio],
  );

  // Disconnect a whole row (no accountId) or a single account of a multiply-linked row. Native rows
  // delete the backing calendar token(s); Composio rows revoke the connected account(s).
  const disconnect = useCallback(
    async (connector: ConnectorStatus, name: string, accountId?: string) => {
      const busyKey = accountId ?? connector.toolkit;
      if (busyRef.current.has(busyKey)) return;
      logPageClickBi({ ...CONNECTORS_PAGE, button_name: `disconnect_${connector.toolkit}` });
      // The routines warning rides the WHOLE-connector disconnect only. Removing one account of a
      // multiply-linked row leaves the connector connected, so nothing is deleted and saying otherwise
      // would talk someone out of an action that costs them nothing.
      //
      // Two whole sentences rather than a base string plus an appended clause: a clause bolted onto a
      // translated sentence lands in the wrong place in most languages, and several cannot take it at
      // all without recasting the verb.
      const removesConnector = !accountId || (connector.accounts ?? []).length <= 1;
      const confirmKey = disconnectConfirmKey(connector.provider, removesConnector);
      const confirmed = await confirm({
        message: t(confirmKey, { name, productName }),
        confirmLabel: t('common.disconnect'),
        destructive: true,
        bi: { ...CONNECTORS_PAGE, modal_name: 'disconnect_connector' },
      });
      if (!confirmed) return;
      if (!claimBusy(busyKey)) return;
      setActionError(null);
      try {
        if (connector.provider === 'native') {
          const ids = accountId ? [accountId] : (connector.accounts ?? []).map((account) => account.id);
          await disconnectCalendarAccounts(ids);
        } else if (connector.provider === 'mcp') {
          // MCP rows carry the McpConnectionService connection ids as their accounts.
          const ids = accountId ? [accountId] : (connector.accounts ?? []).map((account) => account.id);
          for (const id of ids) {
            await deleteMcpConnection(id);
          }
        } else if (connector.provider === 'wix-service') {
          await disconnectConnector(connector.toolkit);
        } else {
          await disconnectConnector(connector.toolkit, accountId);
        }
        await refreshRef.current();
      } catch {
        setActionError(`Couldn't disconnect ${name}. Please try again.`);
        // A row can hold several accounts and they're removed one at a time, so a mid-way failure
        // leaves some already gone — re-read rather than keep rendering rows that no longer exist.
        await refreshRef.current();
      } finally {
        releaseBusy(busyKey);
      }
    },
    [claimBusy, confirm, logPageClickBi, productName, releaseBusy, t],
  );

  return {
    busy,
    actionError,
    icloudOpenFor,
    appleId,
    applePassword,
    setAppleId,
    setApplePassword,
    submitICloud,
    connect,
    addAnother,
    disconnect,
  };
}
